import $ from "jquery";

// Calculate actual text content width using a temporary off-screen measurement element.
// Fixes fraction bar stretching beyond screen width on mobile.
const calculateContentWidth = (mathElement) => {
  if (!mathElement) return 0;

  const { fontSize, fontFamily, fontWeight, letterSpacing } = window.getComputedStyle(mathElement);

  const measureSpan = Object.assign(document.createElement('span'), {
    textContent: mathElement.textContent,
  });

  Object.assign(measureSpan.style, {
    visibility: 'hidden',
    position: 'absolute',
    whiteSpace: 'nowrap',
    fontSize,
    fontFamily: fontFamily || '"math", serif',
    fontWeight,
    letterSpacing,
  });

  document.body.appendChild(measureSpan);
  try {
    return measureSpan.offsetWidth;
  } finally {
    measureSpan.remove();
  }
};

const updateOverflowState = (wrapper, container) => {
  const mathElement = container.querySelector('math');

  if (mathElement) {
    const actualContentWidth = calculateContentWidth(mathElement);
    const wrapperWidth = wrapper.clientWidth;
    const viewportWidth = window.innerWidth;
    const constrainedWidth = Math.min(wrapperWidth, viewportWidth) - 20;

    // If actual content is wider than available space, fix container and expand math
    if (actualContentWidth > constrainedWidth && constrainedWidth > 0) {
      // Fix the container width so it doesn't expand with content
      container.style.width = `${constrainedWidth}px`;
      container.style.maxWidth = `${constrainedWidth}px`;

      // Remove constraint on math element so it can expand
      mathElement.style.maxWidth = 'none';
      mathElement.style.width = 'auto';

      // Expand mtext and mi elements by setting explicit min-width based on text content
      // Only apply to elements wider than screen
      const textElements = mathElement.querySelectorAll('mi, mtext');
      textElements.forEach(el => {
        const text = el.textContent || '';
        if (text.length > 20) {
          const textWidth = calculateContentWidth(el);
          // Only set min-width if text is wider than container
          if (textWidth > constrainedWidth) {
            const paddedWidth = Math.ceil(textWidth * 1.02);
            el.style.maxWidth = 'none';
            el.style.minWidth = `${paddedWidth}px`;
            el.style.display = 'inline-block';
          }
        }
      });
    }
  }

  const hasOverflow = container.scrollWidth > container.clientWidth + 1;
  const atEnd = container.scrollLeft + container.clientWidth >= container.scrollWidth - 1;

  console.log(`MathML overflow check - scrollWidth: ${container.scrollWidth}, clientWidth: ${container.clientWidth}, hasOverflow: ${hasOverflow}, atEnd: ${atEnd}`);

  // Handle hint visibility
  const existingHint = wrapper.querySelector('.ditaMathHint');
  if (hasOverflow && !existingHint) {
    const hint = document.createElement('small');
    hint.className = 'ditaMathHint';
    hint.setAttribute('aria-hidden', 'true');
    hint.innerHTML = 'Swipe to see more <span class="ditaMathHintIcons"><i class="tg-icons-ChevronRight size-8"></i><i class="tg-icons-ChevronRight size-8"></i></span>';
    wrapper.insertBefore(hint, wrapper.firstChild);
  } else if (!hasOverflow && existingHint) {
    existingHint.remove();
  }

  // Handle fade effect
  if (hasOverflow && !atEnd) {
    wrapper.classList.add('ditaMathFade');
  } else {
    wrapper.classList.remove('ditaMathFade');
  }
};

const createUpdate = (wrapper, container) => {
  let frameId;
  return () => {
    if (frameId) {
      cancelAnimationFrame(frameId);
    }
    frameId = requestAnimationFrame(() => {
      updateOverflowState(wrapper, container);
    });
  };
};

const applyRhsClass = (element) => {
  const mathElement = element.querySelector('math') || element;
  if (!mathElement) return;

  const existing = mathElement.querySelectorAll('.ditaMathRhs');
  existing.forEach(node => node.classList.remove('ditaMathRhs'));

  const moElements = mathElement.querySelectorAll('mo');
  let equalsNode = null;
  for (const mo of moElements) {
    if (mo.textContent?.trim() === '=') {
      equalsNode = mo;
      break;
    }
  }

  if (!equalsNode || !equalsNode.parentElement) return;

  const siblings = Array.from(equalsNode.parentElement.children);
  const equalsIndex = siblings.indexOf(equalsNode);
  if (equalsIndex === -1) return;

  siblings.slice(equalsIndex + 1).forEach(node => node.classList.add('ditaMathRhs'));
};

let PrepareMathML = () => {
  //console.log("=== PrepareMathML START ===");
  const mathmlElements = $("mathml");

  mathmlElements.each((index, element) => {
    // console.log(`Processing MathML element ${index}:`, element.tagName, element.className);
    const $element = $(element);

    const existingWrapper = $element.closest('.ditaMathWrapper');
    const existingContainer = $element.closest('.ditaMath');

    if (existingWrapper.length > 0 && existingContainer.length > 0) {
      // console.log(`Element ${index} already wrapped, running overflow check on existing wrapper`);
      updateOverflowState(existingWrapper[0], existingContainer[0]);
      return;
    }

    const $wrapper = $('<span class="ditaMathWrapper"></span>');
    const $scrollContainer = $('<span class="ditaMath"></span>');

    $element.wrap($scrollContainer);
    $element.parent().wrap($wrapper);

    const wrapper = $element.closest('.ditaMathWrapper')[0];
    const container = $element.closest('.ditaMath')[0];

    if (!container || !wrapper) {
      // console.log(`Element ${index} - wrapper or container not found, skipping`);
      return;
    }

    const update = createUpdate(wrapper, container);

    update();
    applyRhsClass(element);

    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(update);
      resizeObserver.observe(container);
    }

    container.addEventListener('scroll', update, { passive: true });
    window.addEventListener('orientationchange', update);
  });
};

export default PrepareMathML;
